import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { customersApi } from "./customers-api";
import type { CustomersFilter } from "./types";

const KEY = ["customers"];

export function useCustomers(filter: CustomersFilter = {}, enabled = true) {
	return useQuery({
		queryKey: [...KEY, "list", filter],
		queryFn: () => customersApi.list(filter),
		enabled,
		placeholderData: (prev) => prev,
	});
}

export function useCustomer(id: string | undefined, reason?: string) {
	return useQuery({
		queryKey: [...KEY, "detail", id],
		queryFn: () => customersApi.get(id!, reason),
		enabled: !!id,
	});
}

export function useCreateCustomer() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (payload: Record<string, unknown>) =>
			customersApi.create(payload),
		onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "list"] }),
	});
}

export function useAdjustLoyalty(id: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (v: { points: number; reason: string; approvalId?: string }) =>
			customersApi.adjustLoyalty(id, v.points, v.reason, v.approvalId),
		onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "detail", id] }),
	});
}

export function useAddNote(id: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (content: string) => customersApi.addNote(id, content),
		onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "detail", id] }),
	});
}

export function useLoyaltyTiers() {
	return useQuery({
		queryKey: [...KEY, "tiers"],
		queryFn: () => customersApi.tiers(),
	});
}

export function useLoyaltyLiability() {
	return useQuery({
		queryKey: [...KEY, "liability"],
		queryFn: () => customersApi.liability(),
	});
}

export function useLoyaltyRewards() {
	return useQuery({
		queryKey: [...KEY, "rewards"],
		queryFn: () => customersApi.rewards(),
	});
}

export function useCreateReward() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (payload: Record<string, unknown>) =>
			customersApi.createReward(payload),
		onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "rewards"] }),
	});
}

export function useSegments() {
	return useQuery({
		queryKey: [...KEY, "segments"],
		queryFn: () => customersApi.segments(),
	});
}

export function useSegmentPreview() {
	return useMutation({
		mutationFn: (rules: unknown) => customersApi.segmentPreview(rules),
	});
}

export function useCreateSegment() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (payload: Record<string, unknown>) =>
			customersApi.createSegment(payload),
		onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "segments"] }),
	});
}

export function useReferrals(status?: string) {
	return useQuery({
		queryKey: [...KEY, "referrals", status],
		queryFn: () => customersApi.referrals(status),
	});
}

export function useReferralLeaderboard() {
	return useQuery({
		queryKey: [...KEY, "leaderboard"],
		queryFn: () => customersApi.leaderboard(),
	});
}

export function useReferralConfig() {
	return useQuery({
		queryKey: [...KEY, "referral-config"],
		queryFn: () => customersApi.referralConfig(),
	});
}

export function useGdprRequests(status?: string) {
	return useQuery({
		queryKey: [...KEY, "gdpr", status],
		queryFn: () => customersApi.gdprRequests(status),
	});
}

export function useGdprSlaReport() {
	return useQuery({
		queryKey: [...KEY, "gdpr-sla"],
		queryFn: () => customersApi.gdprSlaReport(),
	});
}

export function useCustomersStats() {
	return useQuery({
		queryKey: [...KEY, "stats"],
		queryFn: () => customersApi.stats(),
	});
}
